import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { WalletContext } from '../../context/WalletContext';
import WalletPickerModal from '../WalletPickerModal';
import {
  ARBITRUM_CHAIN_ID, ARBITRUM_SEPOLIA_CHAIN_ID, getNetworkConfig, MIN_POSITION_SIZE_USD,
  EXECUTION_FEE_FALLBACK_WEI, CHAINLINK_FEEDS, PYTH_CROSSCHECK_ENABLED,
} from './constants';
import {
  ERC20_ABI,
  getUsdcBalance, ensureUsdcApproved, buildMarketIncreaseOrder,
  executeGmxOrder, fetchExecutionFee,
  fetchOpenPositions, buildMarketDecreaseOrder, executeGmxDecreaseOrder,
} from './gmxUtils';
import axios from 'axios';
import { switchChain } from '../../utils/chainSwitch';

const ANALYTICS_TTL = 2 * 60 * 1000;

// Independent price sources for the pre-trade cross-check. Pyth is primary (most accurate,
// keeper-grade oracle) via our backend /proxy/pyth, which holds the API key server-side — Hermes
// requires auth since 2026-08-26, so the key must never ship in the browser. Coinbase spot is the
// fallback (free, CORS, covers BTC/ETH/XAU). Each returns a USD price or null; neither throws.
async function _fromPyth(feedId) {
  if (!feedId) return null;
  try {
    const res = await axios.get(`${API_BASE}/proxy/pyth?id=${feedId}`, { timeout: 6000 });
    const p = res.data?.parsed?.[0]?.price;
    return p ? parseFloat(p.price) * Math.pow(10, p.expo) : null;
  } catch { return null; }
}
async function _fromCoinbase(symbol) {
  if (!symbol) return null;
  try {
    const res = await axios.get(`https://api.coinbase.com/v2/prices/${symbol}-USD/spot`, { timeout: 5000 });
    const v = parseFloat(res.data?.data?.amount);
    return v > 0 ? v : null;
  } catch { return null; }
}
const _CHAINLINK_ABI = ['function latestRoundData() view returns (uint80,int256,uint256,uint256,uint80)'];
async function _fromChainlink(symbol, provider) {
  const addr = CHAINLINK_FEEDS[symbol];
  if (!addr || !provider) return null;
  try {
    const [, answer, , updatedAt] = await new ethers.Contract(addr, _CHAINLINK_ABI, provider).latestRoundData();
    if (Date.now() / 1000 - updatedAt.toNumber() > 3600) return null;   // stale (>1h heartbeat) → skip
    return parseFloat(ethers.utils.formatUnits(answer, 8));             // Chainlink USD feeds are 8-decimals
  } catch { return null; }
}
// Cross-check the market price against independent sources: Coinbase (free, fast), then Chainlink
// on-chain (free, oracle-grade, via the wallet provider), then Pyth (gated off — see
// PYTH_CROSSCHECK_ENABLED in constants.js).
// Returns { diverged, delta, oraclePrice, source }, or { unreachable: true } when none answered —
// callers WARN on unreachable, never block (a close must never be trapped by a price-check outage).
async function crossCheckPrice(mkt, price, provider) {
  let oraclePrice = await _fromCoinbase(mkt?.symbol), source = 'Coinbase';
  if (!(oraclePrice > 0)) { oraclePrice = await _fromChainlink(mkt?.symbol, provider); source = 'Chainlink'; }
  if (!(oraclePrice > 0) && PYTH_CROSSCHECK_ENABLED) {
    oraclePrice = await _fromPyth(mkt?.pythFeedId); source = 'Pyth';
  }
  if (!(oraclePrice > 0)) return { unreachable: true };
  const delta = Math.abs(price - oraclePrice) / oraclePrice;
  return { unreachable: false, diverged: delta > 0.02, delta, oraclePrice, source };
}

function modeColor(mode, isHold) {
  if (isHold) return '#f7c948';
  if (mode === 'buy')  return '#26c49a';
  if (mode === 'sell') return '#c0392b';
  return '#555';
}

function modeFrame(mode, isHold) {
  if (isHold)          return { bg: 'rgba(247,201,72,0.10)',  border: 'rgba(247,201,72,0.45)' };
  if (mode === 'buy')  return { bg: 'rgba(38,196,154,0.10)',  border: 'rgba(38,196,154,0.45)' };
  if (mode === 'sell') return { bg: 'rgba(192,57,43,0.10)',   border: 'rgba(192,57,43,0.45)'  };
  return               { bg: 'rgba(85,85,85,0.08)',           border: 'rgba(85,85,85,0.25)'   };
}
function modeLabel(mode, isHold) {
  if (isHold) return 'HOLD';
  if (mode === 'buy')  return 'BUY';
  if (mode === 'sell') return 'SELL';
  return '—';
}

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

const LEVERAGES   = [2, 5, 10, 20, 50];
const ORDER_TYPES = ['market', 'limit'];

export default function GmxPanel({ market, setMarket }) {
  const { connected, address, chainId, signer, provider, holdings, connectWallet, disconnectWallet } = useContext(WalletContext);
  const [side,         setSide]         = useState('long');
  const [orderType,    setOrderType]    = useState('market');
  const [leverage,     setLeverage]     = useState(2);
  const [collateral,   setCollateral]   = useState('');
  const [triggerPrice, setTriggerPrice] = useState('');
  const [prices,       setPrices]       = useState({});
  const [usdcBal,      setUsdcBal]      = useState(null);
  const [balLoading,   setBalLoading]   = useState(false);
  const [status,       setStatus]       = useState('');
  const [loading,      setLoading]      = useState(false);
  const [analytics,    setAnalytics]    = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [positions,    setPositions]    = useState([]);
  const [posLoading,   setPosLoading]   = useState(false);
  const [pendingClose,  setPendingClose]  = useState(null);
  const [closeStatus,   setCloseStatus]   = useState('');
  const [usdcAllowance, setUsdcAllowance] = useState(null);
  const [approving,     setApproving]     = useState(false);
  const [txHash,        setTxHash]        = useState(null);
  const [showPicker,    setShowPicker]    = useState(false);
  const [ethBal,        setEthBal]        = useState(null);
  const analyticsTsRef  = useRef(0);
  const keeperPollRef   = useRef(null);
  const wsRef           = useRef(null);
  const wsReconnRef     = useRef(null);
  const wsMountedRef    = useRef(true);

  const { contracts, tokens, markets, isTestnet } = getNetworkConfig(chainId);
  const MARKET_KEYS = Object.keys(markets).filter(k => k !== 'ETH/USD');
  const onArbitrum  = chainId === ARBITRUM_CHAIN_ID || chainId === ARBITRUM_SEPOLIA_CHAIN_ID;
  const mkt         = markets[market] || markets['BTC/USD'];
  const price       = prices[mkt.cgId] || 0;
  const collAmt     = parseFloat(collateral) || 0;
  const trigAmt     = parseFloat(triggerPrice) || 0;
  const execPrice   = orderType === 'limit' && trigAmt > 0 ? trigAmt : price;
  const sizeUsd     = collAmt * leverage;

  // Approval is needed when the allowance can't cover THIS order. The old gate
  // compared against a flat 100 USDC, but approvals are granted for the exact
  // collateral — so any order under $100 approved fine and still showed the
  // APPROVE button, with no way to reach the trade button.
  const collateralRaw = collAmt > 0
    ? ethers.utils.parseUnits(collAmt.toFixed(6), 6)
    : ethers.BigNumber.from(0);
  const needsApproval = usdcAllowance !== null && collAmt > 0 && usdcAllowance.lt(collateralRaw);
  const liqEst      = side === 'long'
    ? execPrice * (1 - (0.9 / leverage))
    : execPrice * (1 + (0.9 / leverage));

  // ── fetch analytics (R values + volume profile) ──────────────────────────
  const fetchAnalytics = useCallback(async () => {
    if (Date.now() - analyticsTsRef.current < ANALYTICS_TTL && analytics) return;
    try {
      const res = await axios.get(`${API_BASE}/btc_today`, {
        timeout: 10000,
        headers: { 'ngrok-skip-browser-warning': 'true' },
      });
      setAnalytics({ rc: res.data.r_current, vp: res.data.volume_profile });
      analyticsTsRef.current = Date.now();
    } catch { /* silent — stale data stays */ }
  }, [analytics]);

  useEffect(() => {
    fetchAnalytics();
    const t = setInterval(fetchAnalytics, ANALYTICS_TTL);
    return () => clearInterval(t);
  }, [fetchAnalytics]);

  // ── prices: REST seed → WebSocket real-time → REST fallback if WS blocked ──
  useEffect(() => {
    wsMountedRef.current = true;
    let wsFails = 0;
    let restPollInterval = null;

    const fetchRest = async () => {
      try {
        const [btcRes, ethRes] = await Promise.all([
          axios.get(`${API_BASE}/proxy/ticker?symbol=BTCUSDT`, { timeout: 6000 }),
          axios.get(`${API_BASE}/proxy/ticker?symbol=ETHUSDT`, { timeout: 6000 }),
        ]);
        const btc = parseFloat(btcRes.data.lastPrice || btcRes.data.price);
        const eth = parseFloat(ethRes.data.lastPrice || ethRes.data.price);
        if (btc) setPrices(prev => ({ ...prev, bitcoin:  prev.bitcoin  || btc }));
        if (eth) setPrices(prev => ({ ...prev, ethereum: prev.ethereum || eth }));
      } catch {}
    };

    // Seed immediately so price is never stuck at 0 on page load
    fetchRest();

    function connect() {
      if (!wsMountedRef.current) return;
      // If WS has failed 3+ times, switch to REST polling instead
      if (wsFails >= 3) {
        if (!restPollInterval) {
          restPollInterval = setInterval(() => {
            axios.get(`${API_BASE}/proxy/ticker?symbol=BTCUSDT`, { timeout: 6000 })
              .then(r => { const p = parseFloat(r.data.lastPrice || r.data.price); if (p) setPrices(prev => ({ ...prev, bitcoin: p })); })
              .catch(() => {});
            axios.get(`${API_BASE}/proxy/ticker?symbol=ETHUSDT`, { timeout: 6000 })
              .then(r => { const p = parseFloat(r.data.lastPrice || r.data.price); if (p) setPrices(prev => ({ ...prev, ethereum: p })); })
              .catch(() => {});
          }, 10_000);
        }
        return;
      }
      const ws = new WebSocket(
        'wss://stream.binance.com:9443/stream?streams=btcusdt@ticker/ethusdt@ticker'
      );
      wsRef.current = ws;
      ws.onmessage = e => {
        try {
          const { stream, data } = JSON.parse(e.data);
          const p = parseFloat(data.c);
          if (!p) return;
          if (stream.startsWith('btcusdt'))      setPrices(prev => ({ ...prev, bitcoin:  p }));
          else if (stream.startsWith('ethusdt')) setPrices(prev => ({ ...prev, ethereum: p }));
        } catch {}
      };
      ws.onclose = () => {
        wsRef.current = null;
        wsFails++;
        if (wsMountedRef.current) wsReconnRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
    }

    connect();
    return () => {
      wsMountedRef.current = false;
      clearTimeout(wsReconnRef.current);
      clearInterval(restPollInterval);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── refresh USDC balance ──────────────────────────────────────────────────
  const refreshBal = useCallback(async () => {
    // Every early return here used to be silent, so a blank balance gave no clue
    // which precondition failed. Log the gate state instead of guessing.
    if (!connected || !address || !onArbitrum) {
      console.log('[GmxPanel] balance skipped —',
        { connected, address: address || null, chainId, onArbitrum });
      return;
    }
    setBalLoading(true);
    try {
      const prov = provider || signer?.provider;
      if (!prov) {
        console.warn('[GmxPanel] no provider — balance forced to 0',
          { hasProvider: Boolean(provider), hasSigner: Boolean(signer) });
        setUsdcBal(0);
        return;
      }
      const b = await getUsdcBalance(prov, address, chainId);
      console.log('[GmxPanel] USDC balance:', b, 'chain', chainId);
      setUsdcBal(b);
    } catch (err) {
      console.error('[GmxPanel] balance fetch failed:', err);
      setUsdcBal(0);
    } finally {
      setBalLoading(false);
    }
  }, [connected, address, onArbitrum, chainId, provider, signer]);

  useEffect(() => {
    refreshBal();
    const t = setInterval(refreshBal, 30_000);
    return () => clearInterval(t);
  }, [refreshBal]);

  // ── USDC allowance + ETH balance check ───────────────────────────────────
  const checkAllowance = useCallback(async () => {
    if (!connected || !address || !onArbitrum) return;
    const prov = provider || signer?.provider;
    // ETH balance — fetch independently so a USDC contract failure doesn't hide it
    try {
      const ethRaw = await prov.getBalance(address);
      setEthBal(parseFloat(ethers.utils.formatEther(ethRaw)));
    } catch {}
    // USDC allowance
    try {
      const usdc = new ethers.Contract(tokens.USDC.address, ERC20_ABI, prov);
      const al = await usdc.allowance(address, contracts.Router);   // Router is the spender, not ExchangeRouter
      setUsdcAllowance(al);
    } catch { setUsdcAllowance(ethers.BigNumber.from(0)); }
  }, [connected, address, onArbitrum, chainId, provider, signer, tokens, contracts]);

  useEffect(() => {
    checkAllowance();
    const t = setInterval(checkAllowance, 30_000);
    return () => clearInterval(t);
  }, [checkAllowance]);

  const approveUsdc = async () => {
    if (collAmt <= 0) { setStatus('Enter collateral amount first, then approve'); return; }
    setApproving(true);
    setStatus('Check wallet — approve USDC…');
    try {
      const collateralRaw = ethers.utils.parseUnits(collAmt.toFixed(6), 6);
      const usdc = new ethers.Contract(tokens.USDC.address, ERC20_ABI, signer);
      const tx = await usdc.approve(contracts.Router, collateralRaw);
      setStatus('Approval submitted — waiting for confirmation…');
      await tx.wait(2);
      await checkAllowance();
      setStatus('USDC approved. Ready to trade.');
    } catch (err) {
      setStatus(err?.message?.slice(0, 80) || 'Approval failed');
    } finally {
      setApproving(false);
    }
  };

  // ── fetch open positions ──────────────────────────────────────────────────
  const fetchPositions = useCallback(async () => {
    if (!connected || !address || !onArbitrum) return;
    setPosLoading(true);
    try {
      const prov = provider || signer?.provider;
      const { markets } = getNetworkConfig(chainId);
      const pos = await fetchOpenPositions(prov, address, chainId, markets);
      setPositions(pos);
    } catch (err) {
      console.error('[GmxPanel] fetchPositions:', err);
    } finally {
      setPosLoading(false);
    }
  }, [connected, address, onArbitrum, chainId, provider, signer]);

  useEffect(() => {
    fetchPositions();
    const t = setInterval(fetchPositions, 30000);
    return () => {
      clearInterval(t);
      if (keeperPollRef.current) clearInterval(keeperPollRef.current);
    };
  }, [fetchPositions]);


  // ── execute — validates + Pyth price check, then shows confirmation modal ──
  const execute = async () => {
    // Was a silent return — the button simply did nothing with no explanation
    if (!signer || !address) { setStatus('Wallet not connected — reconnect and try again'); return; }
    if (collAmt <= 0) { setStatus('Enter collateral amount'); return; }
    if (balLoading)   { setStatus('Fetching balance… try again'); return; }
    if (usdcBal !== null && collAmt > usdcBal + 0.01) {
      setStatus(`Insufficient USDC — wallet shows $${usdcBal.toFixed(2)} (need native USDC on Arbitrum)`);
      return;
    }
    if (price <= 0)                            { setStatus('Price not available — wait a moment'); return; }
    if (orderType === 'limit' && trigAmt <= 0) { setStatus('Enter trigger price'); return; }
    if (sizeUsd < MIN_POSITION_SIZE_USD) {
      setStatus(`Min position size is $${MIN_POSITION_SIZE_USD} — increase collateral or leverage (current: $${sizeUsd.toFixed(2)})`);
      return;
    }
    let warn = null, blocking = false;
    setLoading(true);
    setStatus('Checking fee & price…');

    // F-05: Compare ETH balance against the actual estimated execution fee
    let estimatedFeeWei = ethers.BigNumber.from(EXECUTION_FEE_FALLBACK_WEI);
    try { estimatedFeeWei = await fetchExecutionFee(signer.provider, chainId); } catch { /* use fallback */ }
    const feeEth = parseFloat(ethers.utils.formatEther(estimatedFeeWei));
    if (ethBal !== null && ethBal < feeEth * 1.1) {
      setStatus(`Need ETH for execution fee — est. ${feeEth.toFixed(6)} ETH, wallet has ${ethBal.toFixed(6)} ETH. Bridge ETH to Arbitrum.`);
      setLoading(false);
      return;
    }

    // Price cross-check vs Coinbase spot — a real divergence blocks; an unreachable check only warns
    {
      const chk = await crossCheckPrice(mkt, price, signer?.provider);
      if (chk.diverged) {
        warn = `Price divergence: CG $${Math.round(price).toLocaleString()} vs ${chk.source} $${Math.round(chk.oraclePrice).toLocaleString()} (${(chk.delta * 100).toFixed(1)}%) — wait for prices to sync`;
        blocking = true;
      } else if (chk.unreachable) {
        warn = 'Price cross-check unavailable — proceeding with market price. Review before confirming.';
      }
    }

    setPendingOrder({ collAmt, sizeUsd, leverage, side, execPrice, liqEst, warn, blocking });
    setStatus('');
    setLoading(false);
  };

  // ── executeConfirmed — called from modal after user reviews order details ──
  const executeConfirmed = async () => {
    const po = pendingOrder;
    setPendingOrder(null);
    setLoading(true);
    try {
      const collateralRaw = ethers.utils.parseUnits(po.collAmt.toFixed(6), 6);

      // F-01: Ensure exact allowance is in place before sending order
      setStatus('Checking USDC allowance…');
      await ensureUsdcApproved(signer, collateralRaw, chainId);

      setStatus('Check wallet — confirm order tx…');

      const executionFeeWei = await fetchExecutionFee(signer.provider, chainId);

      const sizeDeltaUsd = ethers.utils.parseUnits(po.sizeUsd.toFixed(6), 24)
        .mul(ethers.BigNumber.from('1000000'));

      const orderParams = buildMarketIncreaseOrder({
        account:              address,
        marketToken:          mkt.marketToken,
        collateralToken:      tokens.USDC.address,
        collateralAmountRaw:  collateralRaw,
        sizeDeltaUsd,
        currentPriceUsd:      po.execPrice,
        isLong:               po.side === 'long',
        executionFeeWei,
        indexTokenDecimals:   mkt.indexTokenDecimals ?? 18,
        isLimit:              orderType === 'limit',
        triggerPriceUsd:      orderType === 'limit' ? trigAmt : 0,
      });

      const tx = await executeGmxOrder(signer, orderParams, tokens.USDC.address, collateralRaw, executionFeeWei, chainId);
      setTxHash(tx.hash);
      setStatus('Submitted — waiting for on-chain confirmation…');

      const receipt = await tx.wait(1);
      if (receipt.status === 0) {
        setStatus('Transaction reverted — see Arbiscan link below');
        return;
      }

      setCollateral('');
      setTriggerPrice('');
      refreshBal();
      const prevCount = positions.length;
      setStatus('Confirmed — keeper executing (~5–30s). Watching for position…');
      pollUntilPosition(prevCount, setStatus);
    } catch (err) {
      console.error('[GmxPanel]', err);
      setStatus(err?.reason || err?.message || 'Order failed');
    } finally {
      setLoading(false);
    }
  };

  // ── rapid position poll after order submit ────────────────────────────────
  const pollUntilPosition = (prevCount, setStatusFn) => {
    if (keeperPollRef.current) clearInterval(keeperPollRef.current);
    let attempts = 0;
    const MAX = 36; // 3 min at 5s intervals
    keeperPollRef.current = setInterval(async () => {
      attempts++;
      try {
        const prov = provider || signer?.provider;
        const { markets } = getNetworkConfig(chainId);
        const pos = await fetchOpenPositions(prov, address, chainId, markets);
        setPositions(pos);
        if (pos.length !== prevCount || attempts >= MAX) {
          clearInterval(keeperPollRef.current);
          keeperPollRef.current = null;
          if (pos.length !== prevCount) setStatusFn('');
          else setStatusFn('Position not yet visible — refresh manually if needed');
        }
      } catch { /* keep polling */ }
    }, 5000);
  };

  // ── close full position ───────────────────────────────────────────────────
  const executeClose = async () => {
    const pos = pendingClose;
    setPendingClose(null);
    setCloseStatus('Verifying close price…');
    try {
      // Cross-check the close price vs Coinbase spot. A real divergence blocks (prices out of sync
      // = bad fill); an unreachable check only warns — a close must NEVER be trapped by a price-check
      // outage. The order uses the market price, not an oracle value.
      const closePrice = price;
      const chk = await crossCheckPrice(pos.mkt, price, signer?.provider);
      if (chk.diverged) {
        setCloseStatus(`Price divergence: feed $${Math.round(price).toLocaleString()} vs ${chk.source} $${Math.round(chk.oraclePrice).toLocaleString()} (${(chk.delta * 100).toFixed(1)}%) — retry when prices sync`);
        return;
      }
      if (chk.unreachable) {
        setCloseStatus('Price cross-check unavailable — closing at market price…');
      }

      const executionFeeWei = await fetchExecutionFee(signer.provider, chainId);
      const feeEth = parseFloat(ethers.utils.formatEther(executionFeeWei));
      if (ethBal !== null && ethBal < feeEth * 1.1) {
        setCloseStatus(`Need ETH for close fee — est. ${feeEth.toFixed(6)} ETH, wallet has ${ethBal.toFixed(6)} ETH. Bridge ETH to Arbitrum.`);
        return;
      }
      const orderParams = buildMarketDecreaseOrder({
        account:             address,
        marketToken:         pos.marketToken,
        collateralToken:     pos.collateralToken,
        sizeDeltaUsd:        pos.sizeInUsdRaw,
        currentPriceUsd:     closePrice,
        isLong:              pos.isLong,
        executionFeeWei,
        indexTokenDecimals:  pos.mkt?.indexTokenDecimals ?? 18,
      });
      setCloseStatus('Check wallet — confirm close tx…');
      const closeTx = await executeGmxDecreaseOrder(signer, orderParams, executionFeeWei, chainId);
      setTxHash(closeTx.hash);
      setCloseStatus('Submitted — waiting for on-chain confirmation…');

      const closeReceipt = await closeTx.wait(1);
      if (closeReceipt.status === 0) {
        setCloseStatus('Transaction reverted — see Arbiscan link below');
        return;
      }

      refreshBal();
      const prevCount = positions.length;
      setCloseStatus('Confirmed — keeper executing (~5–30s). Watching for update…');
      pollUntilPosition(prevCount, setCloseStatus);
    } catch (err) {
      console.error('[GmxPanel] executeClose:', err);
      setCloseStatus(err?.reason || err?.message?.slice(0, 80) || 'Close failed');
    }
  };

  return (
    <div className="tc-gmx-panel">

      {/* Wallet status bar */}
      {connected ? (
        <div className="tc-wallet-bar">
          <span className="tc-wallet-addr">{address?.slice(0, 6)}…{address?.slice(-4)}</span>
          <button className="tc-wallet-disconnect" onClick={disconnectWallet}>DISCONNECT</button>
        </div>
      ) : (
        <div className="tc-wallet-bar tc-wallet-bar--disconnected">
          <button className="tc-wallet-connect-btn" onClick={() => setShowPicker(true)}>
            CONNECT WALLET
          </button>
          <WalletPickerModal
            open={showPicker}
            onClose={() => setShowPicker(false)}
            onConnected={() => setShowPicker(false)}
          />
        </div>
      )}

      {/* Market selector — always visible */}
      <div className="tc-row">
        <span className="tc-label">MARKET</span>
        <div className="tc-market-tabs">
          {MARKET_KEYS.map(k => (
            <button
              key={k}
              className={`tc-market-tab ${market === k ? 'active' : ''}`}
              onClick={() => setMarket(k)}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      {/* Testnet badge */}
      {isTestnet && (
        <div style={{
          textAlign: 'center', fontSize: 8, letterSpacing: '1px',
          color: '#f7c948', background: 'rgba(247,201,72,0.08)',
          border: '1px solid rgba(247,201,72,0.30)', borderRadius: 2,
          padding: '3px 0', marginBottom: 8,
        }}>
          TESTNET — ARBITRUM SEPOLIA
        </div>
      )}

      {/* Live price — always visible */}
      <div className="tc-row tc-price-row">
        <span className="tc-label">PRICE</span>
        <span className="tc-price-value">
          {price > 0 ? `$${price.toLocaleString(undefined, { minimumFractionDigits: mkt.pricePrecision, maximumFractionDigits: mkt.pricePrecision })}` : '—'}
        </span>
      </div>

      {/* ── Analytics — always visible, no wallet required ── */}
      {analytics?.rc && (<>
        <div className="tc-row tc-summary-row">
          <span className="tc-label">R 4H</span>
          {(() => { const f = modeFrame(analytics.rc.mode_4h, analytics.rc.hold_4h); return (
            <span className="tc-summary-val" style={{
              color: modeColor(analytics.rc.mode_4h, analytics.rc.hold_4h),
              background: f.bg, border: `1px solid ${f.border}`,
              borderRadius: 2, padding: '2px 6px', fontSize: 9,
            }}>
              {analytics.rc.r_4h >= 0 ? '+' : ''}{analytics.rc.r_4h?.toFixed(2) ?? '—'}
            </span>
          ); })()}
        </div>
        <div className="tc-row tc-summary-row">
          <span className="tc-label">R DAILY</span>
          {(() => { const f = modeFrame(analytics.rc.mode_daily, analytics.rc.hold_daily); return (
            <span className="tc-summary-val" style={{
              color: modeColor(analytics.rc.mode_daily, analytics.rc.hold_daily),
              background: f.bg, border: `1px solid ${f.border}`,
              borderRadius: 2, padding: '2px 6px', fontSize: 9,
            }}>
              {analytics.rc.r_daily >= 0 ? '+' : ''}{analytics.rc.r_daily?.toFixed(2) ?? '—'}
            </span>
          ); })()}
        </div>
      </>)}
      {analytics?.vp && (
        <div className="tc-row tc-summary-row" style={{ marginBottom: 14 }}>
          <span className="tc-label">VOL PROFILE</span>
          <span style={{ display: 'flex', gap: 10 }}>
            {[
              { label: 'VAL', value: analytics.vp.val, color: '#888' },
              { label: 'POC', value: analytics.vp.poc, color: '#0ef8f8' },
              { label: 'VAH', value: analytics.vp.vah, color: '#888' },
            ].map(({ label, value, color }) => (
              <span key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                <span style={{ color, fontSize: 8, letterSpacing: '0.8px' }}>{label}</span>
                <span style={{ color, fontSize: 10 }}>{value?.toLocaleString()}</span>
              </span>
            ))}
          </span>
        </div>
      )}

      {/* ── Execution UI — always rendered (shield blocks interaction in dashboard) ── */}
      {(<>
        {/* Order type */}
        <div className="tc-row">
          <span className="tc-label">ORDER TYPE</span>
          <div className="tc-lev-tabs">
            {ORDER_TYPES.map(t => (
              <button
                key={t}
                className={`tc-lev-tab ${orderType === t ? 'active' : ''}`}
                onClick={() => setOrderType(t)}
              >{t.toUpperCase()}</button>
            ))}
          </div>
        </div>

        {/* Long / Short */}
        <div className="tc-row">
          <button
            className={`tc-side-btn long ${side === 'long' ? 'active' : ''}`}
            onClick={() => setSide('long')}
          >LONG</button>
          <button
            className={`tc-side-btn short ${side === 'short' ? 'active' : ''}`}
            onClick={() => setSide('short')}
          >SHORT</button>
        </div>

        {/* Trigger price — limit orders only */}
        {orderType === 'limit' && (
          <div className="tc-row tc-input-row">
            <span className="tc-label">TRIGGER PRICE</span>
            <div className="tc-input-group">
              <input
                type="number"
                className="tc-input"
                placeholder="0.00"
                value={triggerPrice}
                onChange={e => setTriggerPrice(e.target.value)}
                min="0"
              />
              <span className="tc-input-suffix">USD</span>
            </div>
          </div>
        )}

        {/* Collateral input */}
        <div className="tc-row tc-input-row">
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <span className="tc-label">COLLATERAL</span>
            {/*
              Always render the balance once it is known, zero included. Hiding it
              at zero made an empty wallet look identical to a failed read — the
              row simply disappeared, with nothing to say which it was.
            */}
            {usdcBal !== null && (
              <span style={{ fontSize: 8, color: '#444', letterSpacing: '0.5px' }}>
                BAL <span style={{ color: usdcBal > 0 ? '#666' : '#f8b60e' }}>${usdcBal.toFixed(2)}</span>
              </span>
            )}
          </div>
          <div className="tc-input-group">
            <input
              type="number"
              className="tc-input"
              placeholder="0.00"
              value={collateral}
              onChange={e => setCollateral(e.target.value)}
              min="0"
            />
            <span className="tc-input-suffix">USDC</span>
          </div>
          {usdcBal !== null && usdcBal <= 0 && (() => {
            /*
             * Name where the USDC actually is. "No USDC on Arbitrum" is true but
             * reads as wrong when the wallet panel alongside shows a balance —
             * that balance is on another chain, and the two never said so.
             */
            const elsewhere = (holdings || [])
              .filter(h => /^usdc(\.e)?$/i.test((h.symbol || '').trim())
                        && parseFloat(h.balance) > 0
                        && h.chainId !== chainId)
              .map(h => `${parseFloat(h.balance).toFixed(2)} on ${h.chain}`);
            return (
              <div style={{ width: '100%', marginTop: 6, fontSize: 8, color: '#f8b60e', letterSpacing: '0.5px', lineHeight: 1.6 }}>
                NO USDC ON ARBITRUM — GMX settles in native Arbitrum USDC.
                {elsewhere.length > 0 && (
                  <div style={{ color: '#666', marginTop: 3 }}>
                    You hold {elsewhere.join(', ')} — not tradable here. Bridge to Arbitrum to use it.
                  </div>
                )}
              </div>
            );
          })()}
          {usdcBal > 0 && (
            <div style={{ width: '100%', marginTop: 6 }}>
              <input
                type="range" min={0} max={100} step={1}
                value={usdcBal > 0 ? Math.round((collAmt / usdcBal) * 100) : 0}
                onChange={e => {
                  const pct = parseFloat(e.target.value);
                  const amt = Math.floor(usdcBal * pct) / 100;
                  setCollateral(amt > 0 ? amt.toFixed(2) : '');
                }}
                style={{ width: '100%', accentColor: '#26c49a', cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                {[0, 25, 50, 75, 100].map(pct => (
                  <button
                    key={pct}
                    onClick={() => {
                      const amt = Math.floor(usdcBal * pct) / 100;
                      setCollateral(amt > 0 ? amt.toFixed(2) : '');
                    }}
                    style={{
                      background: 'none', border: 'none', padding: '1px 0',
                      fontSize: 8, cursor: 'pointer', fontFamily: 'inherit',
                      letterSpacing: '0.3px',
                      color: Math.round((collAmt / usdcBal) * 100) === pct ? '#26c49a' : '#444',
                    }}
                  >{pct}%</button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Leverage */}
        <div className="tc-row">
          <span className="tc-label">LEVERAGE</span>
          <div className="tc-lev-tabs">
            {LEVERAGES.map(l => (
              <button
                key={l}
                className={`tc-lev-tab ${leverage === l ? 'active' : ''}`}
                onClick={() => setLeverage(l)}
              >{l}×</button>
            ))}
          </div>
        </div>

        {/* Size summary */}
        <div className="tc-row tc-summary-row">
          <span className="tc-label">SIZE</span>
          <span className="tc-summary-val">
            ${sizeUsd > 0 ? sizeUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </span>
        </div>

        {/* Liquidation estimate */}
        {price > 0 && collAmt > 0 && (
          <div className="tc-row tc-summary-row">
            <span className="tc-label">LIQ. EST. <span style={{ fontSize: 7, color: '#444' }}>(excl. fees)</span></span>
            <span className="tc-summary-val tc-liq">
              ~${liqEst.toLocaleString(undefined, { minimumFractionDigits: mkt.pricePrecision, maximumFractionDigits: mkt.pricePrecision })}
            </span>
          </div>
        )}

        {/* Execution fee */}
        <div className="tc-row tc-fee-note">
          <span className="tc-label">EXEC FEE</span>
          <span className="tc-fee-val">dynamic (gas × 1.8M + 30% buffer)</span>
        </div>

        {/* Execute / chain switch / approve */}
        {!connected ? (
          <button className="tc-execute-btn long" disabled style={{ opacity: 0.3 }}>
            CONNECT WALLET ABOVE
          </button>
        ) : !onArbitrum ? (
          <button
            className="tc-execute-btn long"
            onClick={async () => {
              const target = isTestnet ? 421614 : 42161;
              const r = await switchChain(target, provider?.provider || null);
              // Previously this called switchToChain() with no catch, so a rejection
              // died silently and the button looked inert.
              if (!r.ok) {
                setStatus(r.reason === 'rejected'
                  ? 'Network switch rejected in wallet'
                  : `Could not switch network (${r.reason})`);
              }
            }}
          >
            SWITCH TO {isTestnet ? 'ARB SEPOLIA' : 'ARBITRUM'}
          </button>
        ) : needsApproval ? (
          <>
            <button className="tc-execute-btn long" onClick={approveUsdc} disabled={approving}>
              {approving ? 'APPROVING…' : 'APPROVE USDC'}
            </button>
            {status && <div className="tc-status">{status}</div>}
          </>
        ) : (
          <>
            <button
              className={`tc-execute-btn ${side}`}
              onClick={execute}
              disabled={loading || collAmt <= 0}
            >
              {loading ? 'SENDING…' : `${side === 'long' ? 'LONG' : 'SHORT'} ${mkt.symbol}`}
            </button>

            {status && <div className="tc-status">{status}</div>}
            {txHash && (
              <a
                href={`https://arbiscan.io/tx/${txHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="tc-arbiscan-link"
              >↗ Arbiscan</a>
            )}

            <div className="tc-funds">
              <span className="tc-label">
                MY FUNDS
                {' '}<span
                  style={{ cursor: 'pointer', color: balLoading ? '#555' : '#0ef8f8', fontSize: 9 }}
                  onClick={() => { refreshBal(); checkAllowance(); }}
                  title="Refresh balance"
                >↻</span>
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                <span className="tc-funds-val">
                  {balLoading ? '…' : usdcBal === null ? '—' : `$${usdcBal.toFixed(2)} USDC`}
                </span>
                <span className="tc-funds-val" style={{ color: ethBal !== null && ethBal < 0.0003 ? '#c0392b' : '#888', fontSize: 9 }}>
                  {ethBal === null ? 'GAS —' : `GAS ${ethBal.toFixed(5)} ETH`}
                </span>
              </span>
            </div>
          </>
        )}
      </>)}

      {/* ── Open positions ── */}
      {connected && onArbitrum && (
        <div className="tc-positions">
          <div className="tc-positions-header">
            <span className="tc-label">POSITIONS</span>
            <span
              className="tc-positions-refresh"
              onClick={fetchPositions}
              title="Refresh positions"
            >↻</span>
          </div>

          {closeStatus && <div className="tc-status" style={{ margin: '0 0 4px' }}>{closeStatus}</div>}
          {txHash && (
            <a
              href={`https://arbiscan.io/tx/${txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tc-arbiscan-link"
              style={{ display: 'block', marginBottom: 8 }}
            >↗ Arbiscan</a>
          )}

          {posLoading && positions.length === 0 ? (
            <div className="tc-positions-empty">loading…</div>
          ) : positions.length === 0 ? (
            <div className="tc-positions-empty">no open positions</div>
          ) : positions.map(pos => {
            const pnl = pos.entryPrice > 0 && price > 0
              ? (pos.isLong
                ? (price - pos.entryPrice) / pos.entryPrice * pos.sizeUsd
                : (pos.entryPrice - price) / pos.entryPrice * pos.sizeUsd)
              : null;
            const pnlPct = pos.collateralUsd > 0 && pnl !== null
              ? pnl / pos.collateralUsd * 100 : null;
            return (
              <div key={pos.key} className="tc-position-card">
                <div className="tc-position-top">
                  <span className={`tc-position-side ${pos.isLong ? 'long' : 'short'}`}>
                    {pos.marketKey} · {pos.isLong ? 'LONG' : 'SHORT'} {pos.leverage}×
                  </span>
                  <button className="tc-close-btn" onClick={() => setPendingClose(pos)}>CLOSE</button>
                </div>
                <div className="tc-position-stats">
                  <span>
                    <span className="tc-label">SIZE </span>
                    <span className="tc-position-val">
                      ${pos.sizeUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </span>
                  <span>
                    <span className="tc-label">ENTRY </span>
                    <span className="tc-position-val">
                      ${pos.entryPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </span>
                  {pnl !== null && (
                    <span>
                      <span className="tc-label">PNL </span>
                      <span className={`tc-position-pnl ${pnl >= 0 ? 'pos' : 'neg'}`}>
                        {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)} ({pnlPct >= 0 ? '+' : ''}{pnlPct?.toFixed(1)}%)
                      </span>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


      {/* Confirmation modal */}
      {pendingOrder && (
        <div className="tc-modal-overlay" onClick={() => setPendingOrder(null)}>
          <div className="tc-modal" onClick={e => e.stopPropagation()}>
            <div className="tc-modal-title">CONFIRM ORDER</div>
            {pendingOrder.warn && (
              <div className="tc-modal-warn">{pendingOrder.warn}</div>
            )}
            <div className="tc-modal-row">
              <span>SIDE</span>
              <span style={{ color: pendingOrder.side === 'long' ? '#26c49a' : '#c0392b', fontWeight: 700 }}>
                {pendingOrder.side.toUpperCase()}
              </span>
            </div>
            <div className="tc-modal-row"><span>COLLATERAL</span><span>${pendingOrder.collAmt.toFixed(2)} USDC</span></div>
            <div className="tc-modal-row"><span>LEVERAGE</span><span>{pendingOrder.leverage}×</span></div>
            <div className="tc-modal-row">
              <span>SIZE</span>
              <span>${pendingOrder.sizeUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
            </div>
            <div className="tc-modal-row">
              <span>ENTRY</span>
              <span>${pendingOrder.execPrice.toLocaleString(undefined, { minimumFractionDigits: mkt.pricePrecision, maximumFractionDigits: mkt.pricePrecision })}</span>
            </div>
            <div className="tc-modal-row">
              <span>LIQ. EST. <span style={{ fontSize: 7, color: '#444' }}>(approx)</span></span>
              <span style={{ color: '#e67e22' }}>
                ~${pendingOrder.liqEst.toLocaleString(undefined, { minimumFractionDigits: mkt.pricePrecision, maximumFractionDigits: mkt.pricePrecision })}
              </span>
            </div>
            <div className="tc-modal-btns">
              <button className="tc-modal-cancel" onClick={() => setPendingOrder(null)}>CANCEL</button>
              <button
                className={`tc-modal-confirm ${pendingOrder.side}`}
                onClick={executeConfirmed}
                disabled={pendingOrder.blocking}
              >CONFIRM</button>
            </div>
          </div>
        </div>
      )}

      {/* Close confirmation modal */}
      {pendingClose && (
        <div className="tc-modal-overlay" onClick={() => setPendingClose(null)}>
          <div className="tc-modal" onClick={e => e.stopPropagation()}>
            <div className="tc-modal-title">CONFIRM CLOSE</div>
            <div className="tc-modal-row">
              <span>MARKET</span>
              <span>{pendingClose.marketKey}</span>
            </div>
            <div className="tc-modal-row">
              <span>SIDE</span>
              <span style={{ color: pendingClose.isLong ? '#26c49a' : '#c0392b', fontWeight: 700 }}>
                {pendingClose.isLong ? 'LONG' : 'SHORT'}
              </span>
            </div>
            <div className="tc-modal-row">
              <span>SIZE</span>
              <span>${pendingClose.sizeUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
            <div className="tc-modal-row">
              <span>ENTRY</span>
              <span>${pendingClose.entryPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
            <div className="tc-modal-row">
              <span>CURRENT</span>
              <span>{price > 0 ? `$${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}</span>
            </div>
            <div className="tc-modal-btns">
              <button className="tc-modal-cancel" onClick={() => setPendingClose(null)}>CANCEL</button>
              <button
                className={`tc-modal-confirm ${pendingClose.isLong ? 'short' : 'long'}`}
                onClick={executeClose}
              >CLOSE POSITION</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
